'use client';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, ClipboardList, Building2, Star, QrCode, Shield, Lock, Phone, MessageCircle, ChevronRight, Info, UserCircle, FlaskConical, Pill, ArrowLeft,  } from 'lucide-react';
import Link from 'next/link';
import TopNav from '@/components/layout/TopNav';
import AppImage from '@/components/ui/AppImage';
import { hospitalConfig } from '@/data/hospitalConfig';
import { trackEvent } from '@/lib/analytics';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchAppointments, fetchPrescriptions, fetchLabReports,
  fetchMedicines, fetchVisits, fetchMembership,
  type PassportAppointmentRow, type PassportPrescriptionRow,
  type PassportLabReportRow, type PassportMedicineRow,
  type PassportVisitRow, type PassportMembershipRow,
} from '@/lib/healthPassportService';
import Icon from '@/components/ui/AppIcon';


// ─── Sub-components ───────────────────────────────────────────────────────────

function DemoBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-950/50 border border-amber-900/50 text-amber-400 text-xs font-semibold tracking-wider">
      DEMO
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    upcoming: 'bg-blue-900/30 text-blue-400 border-blue-900/50',
    confirmed: 'bg-green-900/30 text-green-400 border-green-900/50',
    completed: 'bg-white/5 text-white/40 border-white/10',
    cancelled: 'bg-red-900/30 text-red-400 border-red-900/50',
    active: 'bg-green-900/30 text-green-400 border-green-900/50',
    available: 'bg-green-900/30 text-green-400 border-green-900/50',
    pending: 'bg-amber-900/30 text-amber-400 border-amber-900/50',
    processing: 'bg-blue-900/30 text-blue-400 border-blue-900/50',
    inactive: 'bg-white/5 text-white/30 border-white/10',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold tracking-wider uppercase ${map[status] || 'bg-white/5 text-white/40 border-white/10'}`}>
      {status}
    </span>
  );
}

function EmptyState({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center text-center py-12 px-6"
    >
      <div className="w-14 h-14 rounded-2xl bg-white/4 border border-white/8 flex items-center justify-center mb-4">
        <Icon size={22} className="text-white/20" />
      </div>
      <p className="text-sm font-semibold text-white/40 tracking-wide mb-2">{title}</p>
      <p className="text-xs text-white/25 leading-relaxed max-w-xs">{description}</p>
    </motion.div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="flex items-center gap-3 text-white/30">
        <span className="w-4 h-4 border-2 border-white/20 border-t-white/50 rounded-full animate-spin" />
        <span className="text-xs tracking-widest uppercase">Loading...</span>
      </div>
    </div>
  );
}

function SectionHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors tracking-widest uppercase">
        <ArrowLeft size={12} />
      </button>
      <div>
        <p className="text-xs font-semibold tracking-widest text-primary/60 uppercase">DIGITAL HEALTH PASSPORT™</p>
        <h2 className="text-lg font-bold text-white tracking-wide">{title}</h2>
      </div>
    </div>
  );
}

// ─── Section Views ────────────────────────────────────────────────────────────

function AppointmentsView({ onBack }: { onBack: () => void }) {
  const [items, setItems] = useState<PassportAppointmentRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppointments().then((data) => { setItems(data); setLoading(false); });
  }, []);

  return (
    <div>
      <SectionHeader title="MY APPOINTMENTS" onBack={onBack} />
      {loading ? <LoadingState /> : items.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="NO APPOINTMENTS YET"
          description="Your All Is Well appointments will appear here when available."
        />
      ) : (
        <div className="space-y-3">
          {items.map((appt) => (
            <motion.div key={appt.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl bg-card border border-white/8">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {appt.isDemo && <DemoBadge />}
                    <StatusBadge status={appt.status} />
                  </div>
                  <p className="text-sm font-semibold text-white">{appt.doctorName}</p>
                  <p className="text-xs text-primary/70">{appt.department}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-semibold text-white">{appt.appointmentDate}</p>
                  <p className="text-xs text-white/40">{appt.appointmentTime}</p>
                </div>
              </div>
              <p className="text-xs text-white/30 truncate">{appt.centreName}</p>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function PrescriptionsView({ onBack }: { onBack: () => void }) {
  const [items, setItems] = useState<PassportPrescriptionRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrescriptions().then((data) => { setItems(data); setLoading(false); });
  }, []);

  return (
    <div>
      <SectionHeader title="MY PRESCRIPTIONS" onBack={onBack} />
      {loading ? <LoadingState /> : items.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="NO PRESCRIPTIONS YET"
          description="Your All Is Well prescriptions will appear here when available."
        />
      ) : (
        <div className="space-y-3">
          {items.map((rx) => (
            <motion.div key={rx.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl bg-card border border-white/8">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {rx.isDemo && <DemoBadge />}
                    <StatusBadge status={rx.status} />
                  </div>
                  <p className="text-sm font-semibold text-white">{rx.doctorName}</p>
                  <p className="text-xs text-primary/70">{rx.department}</p>
                </div>
                <p className="text-xs text-white/40 flex-shrink-0">{rx.prescriptionDate}</p>
              </div>
              <p className="text-xs text-white/30 mb-2 truncate">{rx.centreName}</p>
              {rx.medicinesCount > 0 && (
                <p className="text-xs text-white/40">{rx.medicinesCount} medicine{rx.medicinesCount !== 1 ? 's' : ''}</p>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function LabReportsView({ onBack }: { onBack: () => void }) {
  const [items, setItems] = useState<PassportLabReportRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLabReports().then((data) => { setItems(data); setLoading(false); });
  }, []);

  return (
    <div>
      <SectionHeader title="MY LAB REPORTS" onBack={onBack} />
      {/* GET MY LAB REPORT action */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-5 p-4 rounded-2xl border"
        style={{ borderColor: 'rgba(201,168,76,0.2)', background: 'linear-gradient(135deg, rgba(107,33,212,0.08), rgba(201,168,76,0.04))' }}
      >
        <p className="text-xs font-semibold tracking-widest text-primary/60 uppercase mb-1">PATIENT SERVICE</p>
        <p className="text-sm font-bold text-white mb-1">GET MY LAB REPORT</p>
        <p className="text-xs text-white/40 leading-relaxed mb-3">
          Verify your identity and receive your latest lab report securely on WhatsApp.
        </p>
        <Link href="/lab-report" className="inline-flex items-center gap-2 btn-outline-gold text-xs px-4 py-2">
          GET REPORT ON WHATSAPP <ChevronRight size={11} />
        </Link>
      </motion.div>

      {loading ? <LoadingState /> : items.length === 0 ? (
        <EmptyState
          icon={FlaskConical}
          title="NO LAB REPORTS YET"
          description="Your All Is Well laboratory reports will appear here when available."
        />
      ) : (
        <div className="space-y-3">
          {items.map((report) => (
            <motion.div key={report.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl bg-card border border-white/8">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {report.isDemo && <DemoBadge />}
                    <StatusBadge status={report.status} />
                  </div>
                  <p className="text-sm font-semibold text-white">{report.testName}</p>
                  <p className="text-xs text-primary/60">{report.category}</p>
                </div>
                <p className="text-xs text-white/40 flex-shrink-0">{report.reportDate}</p>
              </div>
              <p className="text-xs text-white/30 mb-3">{report.centreName}</p>
              {report.status === 'available' && (
                <Link href="/lab-report" className="flex items-center gap-1.5 text-xs font-semibold text-primary/80 hover:text-primary transition-colors tracking-wider uppercase">
                  VIEW REPORT <ChevronRight size={10} />
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function MedicinesView({ onBack }: { onBack: () => void }) {
  const [items, setItems] = useState<PassportMedicineRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMedicines().then((data) => { setItems(data); setLoading(false); });
  }, []);

  return (
    <div>
      <SectionHeader title="MY MEDICINES" onBack={onBack} />
      {loading ? <LoadingState /> : items.length === 0 ? (
        <EmptyState
          icon={Pill}
          title="NO MEDICINES YET"
          description="Your All Is Well medicine history will appear here when available."
        />
      ) : (
        <div className="space-y-3">
          {items.map((med) => (
            <motion.div key={med.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl bg-card border border-white/8">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {med.isDemo && <DemoBadge />}
                    <StatusBadge status={med.status} />
                  </div>
                  <p className="text-sm font-semibold text-white">{med.medicineName}</p>
                  {med.dosage && <p className="text-xs text-primary/70">{med.dosage}</p>}
                </div>
              </div>
              {med.frequency && <p className="text-xs text-white/40 mb-1">{med.frequency}</p>}
              {med.prescribedBy && <p className="text-xs text-white/30">Prescribed by {med.prescribedBy}</p>}
              {(med.startDate || med.endDate) && (
                <p className="text-xs text-white/25 mt-1">{med.startDate}{med.endDate ? ` — ${med.endDate}` : ''}</p>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function VisitsView({ onBack }: { onBack: () => void }) {
  const [items, setItems] = useState<PassportVisitRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVisits().then((data) => { setItems(data); setLoading(false); });
  }, []);

  return (
    <div>
      <SectionHeader title="MY VISITS" onBack={onBack} />
      {loading ? <LoadingState /> : items.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="NO VISITS YET"
          description="Your All Is Well visit history will appear here when available."
        />
      ) : (
        <div className="space-y-3">
          {items.map((visit) => (
            <motion.div key={visit.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-2xl bg-card border border-white/8">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {visit.isDemo && <DemoBadge />}
                  </div>
                  <p className="text-sm font-semibold text-white">{visit.department}</p>
                  {visit.doctorName && <p className="text-xs text-primary/70">{visit.doctorName}</p>}
                </div>
                <p className="text-xs text-white/40 flex-shrink-0">{visit.visitDate}</p>
              </div>
              <p className="text-xs text-white/30 truncate">{visit.centreName}</p>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function MembershipView({ onBack }: { onBack: () => void }) {
  const [membership, setMembership] = useState<PassportMembershipRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMembership().then((data) => { setMembership(data); setLoading(false); });
  }, []);

  return (
    <div>
      <SectionHeader title="MY MEMBERSHIP" onBack={onBack} />
      {loading ? <LoadingState /> : !membership ? (
        <EmptyState
          icon={Star}
          title="NO ACTIVE MEMBERSHIP"
          description="Your All Is Well membership details will appear here when active."
        />
      ) : (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-5 rounded-2xl bg-card border border-white/8">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {membership.isDemo && <DemoBadge />}
            <StatusBadge status={membership.status} />
          </div>
          <p className="text-lg font-bold text-white mb-1">{membership.planName}</p>
          {membership.memberId && <p className="text-xs text-primary/60 font-mono mb-3">{membership.memberId}</p>}
          {membership.validFrom && (
            <p className="text-xs text-white/40">Valid: {membership.validFrom}{membership.validUntil ? ` — ${membership.validUntil}` : ''}</p>
          )}
        </motion.div>
      )}
    </div>
  );
}

// ─── Section map ──────────────────────────────────────────────────────────────

type PassportSection =
  | 'home' | 'id-card' |'appointments'| 'prescriptions' | 'lab-reports' |'medicines' | 'visits' | 'membership';

const sectionCards = [
  { id: 'appointments' as PassportSection, label: 'APPOINTMENTS', icon: CalendarDays, description: 'Upcoming and past appointments' },
  { id: 'prescriptions' as PassportSection, label: 'PRESCRIPTIONS', icon: ClipboardList, description: 'Your prescriptions and medications' },
  { id: 'lab-reports' as PassportSection, label: 'LAB REPORTS', icon: FlaskConical, description: 'Diagnostic and lab reports' },
  { id: 'medicines' as PassportSection, label: 'MEDICINES', icon: Pill, description: 'Your medicine history' },
  { id: 'visits' as PassportSection, label: 'VISITS', icon: Building2, description: 'Your visit history at All Is Well' },
  { id: 'membership' as PassportSection, label: 'MEMBERSHIP', icon: Star, description: 'Your All Is Well membership details' },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HealthPassportContent() {
  const { user, profile, loading: authLoading } = useAuth();
  const [view, setView] = useState<PassportSection>('home');
  const [showDemoNotice, setShowDemoNotice] = useState(true);

  const handleViewIdCard = () => {
    setView('id-card');
    trackEvent({ event: 'journey_started', source: 'health-passport-id-card' });
  };

  const displayName = profile?.fullName || profile?.firstName || 'Patient';
  const patientId = user ? `AIW-${user.id.slice(0, 8).toUpperCase()}` : 'AIW-DEMO-0001';

  return (
    <main className="journey-bg min-h-screen flex flex-col pb-24" role="main" aria-label="Digital Health Passport">
      <TopNav showBackButton backHref="/digital-reception-screen" backLabel="Back" />

      <div className="flex-1 flex flex-col items-center pt-24 px-4 md:px-8">
        <div className="w-full max-w-2xl">

          {/* Demo data notice */}
          <AnimatePresence>
            {showDemoNotice && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="mb-6 p-3 rounded-xl bg-amber-950/40 border border-amber-900/50 flex items-start gap-3"
              >
                <Info size={13} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-amber-400 font-semibold mb-0.5">PROTOTYPE — DEMO DATA ONLY</p>
                  <p className="text-xs text-amber-400/70 leading-relaxed">
                    This is a design prototype. Records marked DEMO are for demonstration only. In production, all information requires secure authentication.
                  </p>
                </div>
                <button
                  onClick={() => setShowDemoNotice(false)}
                  className="text-amber-400/50 hover:text-amber-400 transition-colors text-lg leading-none"
                  aria-label="Dismiss notice"
                >
                  ×
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">

            {/* ── SECTION VIEWS ─────────────────────────────────────────── */}
            {view === 'appointments' && (
              <motion.div key="appointments" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <AppointmentsView onBack={() => setView('home')} />
              </motion.div>
            )}
            {view === 'prescriptions' && (
              <motion.div key="prescriptions" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <PrescriptionsView onBack={() => setView('home')} />
              </motion.div>
            )}
            {view === 'lab-reports' && (
              <motion.div key="lab-reports" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <LabReportsView onBack={() => setView('home')} />
              </motion.div>
            )}
            {view === 'medicines' && (
              <motion.div key="medicines" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <MedicinesView onBack={() => setView('home')} />
              </motion.div>
            )}
            {view === 'visits' && (
              <motion.div key="visits" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <VisitsView onBack={() => setView('home')} />
              </motion.div>
            )}
            {view === 'membership' && (
              <motion.div key="membership" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <MembershipView onBack={() => setView('home')} />
              </motion.div>
            )}

            {/* ── HOME VIEW ─────────────────────────────────────────────── */}
            {view === 'home' && (
              <motion.div
                key="home"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
              >
                {/* Header */}
                <div className="mb-8">
                  <p className="section-label mb-2">DIGITAL HEALTH PASSPORT™</p>
                  <h1 className="text-display-md text-white font-bold leading-tight mb-2">
                    {authLoading ? 'LOADING...' : user ? (
                      <>WELCOME BACK.<br /><span className="gold-gradient-text">{displayName.toUpperCase()}</span></>
                    ) : (
                      <>WELCOME BACK.<br /><span className="gold-gradient-text">YOUR HEALTH</span><br /><span className="text-white">JOURNEY.</span></>
                    )}
                  </h1>
                  <p className="text-muted-foreground text-sm max-w-lg leading-relaxed">
                    Your secure digital health identity with All Is Well.
                  </p>
                </div>

                {/* Patient identity preview */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-r from-card to-card/80 border border-border rounded-2xl p-5 mb-6 flex items-center gap-4"
                >
                  <div className="w-14 h-14 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                    <UserCircle size={28} className="text-primary" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground tracking-widest uppercase mb-0.5">PATIENT</p>
                    <p className="text-lg font-bold text-white">{authLoading ? '...' : displayName}</p>
                    <p className="text-xs text-muted-foreground font-mono">{patientId}</p>
                  </div>
                  <button
                    onClick={handleViewIdCard}
                    className="flex items-center gap-2 btn-outline-gold text-xs px-4 py-2.5 flex-shrink-0"
                    aria-label="View Digital Health Passport ID card"
                  >
                    <QrCode size={13} />
                    <span className="hidden sm:inline">VIEW ID</span>
                  </button>
                </motion.div>

                {/* Login prompt if not authenticated */}
                {!authLoading && !user && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="mb-6 p-4 rounded-xl border border-primary/20 bg-primary/5 flex items-start gap-3"
                  >
                    <Lock size={14} className="text-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-white mb-1">SIGN IN TO VIEW YOUR RECORDS</p>
                      <p className="text-xs text-white/40 leading-relaxed mb-3">
                        Log in to see your appointments, prescriptions, lab reports, and more.
                      </p>
                      <Link
                        href="/my-all-is-well/login"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary/80 hover:text-primary transition-colors tracking-wider uppercase"
                      >
                        LOGIN / SIGN UP <ChevronRight size={11} />
                      </Link>
                    </div>
                  </motion.div>
                )}

                {/* Passport sections */}
                <p className="text-xs text-muted-foreground tracking-widest uppercase mb-4">YOUR HEALTH JOURNEY</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {sectionCards.map((section, idx) => {
                    const IconComp = section.icon;
                    return (
                      <motion.button
                        key={section.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.06 }}
                        onClick={() => user ? setView(section.id) : undefined}
                        className={`premium-option-card p-4 flex items-center gap-3 text-left w-full ${user ? 'cursor-pointer hover:border-primary/30' : 'cursor-default opacity-60'}`}
                        role="button"
                        aria-label={section.label}
                        disabled={!user}
                      >
                        <div className="option-icon-wrap flex-shrink-0">
                          <IconComp size={18} className="text-primary" aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-xs text-foreground tracking-wide">{section.label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{section.description}</p>
                        </div>
                        <div className="flex-shrink-0">
                          {user ? (
                            <ChevronRight size={14} className="text-muted-foreground/50" />
                          ) : (
                            <Lock size={10} className="text-muted-foreground/50" />
                          )}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>

                {/* MY LAB REPORTS — connected action */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-4 rounded-2xl overflow-hidden border"
                  style={{ borderColor: 'rgba(201,168,76,0.2)', background: 'linear-gradient(135deg, rgba(107,33,212,0.08), rgba(201,168,76,0.04))' }}
                >
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                        <FlaskConical size={16} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold tracking-widest text-primary/60 uppercase mb-0.5">LAB REPORTS</p>
                        <p className="text-sm font-bold text-foreground mb-1">MY LAB REPORTS</p>
                        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                          Access your available lab reports securely through WhatsApp.
                        </p>
                        <Link
                          href="/lab-report"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary/80 hover:text-primary transition-colors tracking-wider uppercase"
                          aria-label="Get my lab report"
                        >
                          GET MY LAB REPORT <ChevronRight size={11} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Privacy notice */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mt-8 p-4 rounded-xl bg-card/40 border border-border/40 flex items-start gap-3"
                >
                  <Shield size={14} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-foreground mb-1">PRIVACY & SECURITY</p>
                    <p className="text-xs text-muted-foreground/80 leading-relaxed">
                      Your health information is private. All data requires secure authentication. Records are protected by Row Level Security — only you can see your own records.
                    </p>
                  </div>
                </motion.div>

                {/* Contact */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <a
                    href={hospitalConfig.phoneTel}
                    className="flex items-center justify-center gap-2 btn-outline-gold text-xs px-4 py-3"
                    aria-label="Call All Is Well"
                  >
                    <Phone size={13} />
                    CALL
                  </a>
                  <a
                    href={hospitalConfig.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 btn-ghost-white text-xs px-4 py-3"
                    aria-label="WhatsApp All Is Well"
                  >
                    <MessageCircle size={13} />
                    WHATSAPP
                  </a>
                </div>
              </motion.div>
            )}

            {/* ── ID CARD VIEW ───────────────────────────────────────────── */}
            {view === 'id-card' && (
              <motion.div
                key="id-card"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
              >
                <button
                  onClick={() => setView('home')}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm mb-6"
                  aria-label="Back to passport home"
                >
                  <ChevronRight size={14} className="rotate-180" />
                  <span className="text-xs tracking-widest uppercase">Back</span>
                </button>

                <p className="section-label mb-4">DIGITAL HEALTH PASSPORT™ — ID</p>

                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="relative rounded-2xl overflow-hidden border border-primary/30 shadow-gold"
                  style={{ background: 'linear-gradient(135deg, #0A1628 0%, #0F1F35 40%, #1a0a2e 100%)' }}
                >
                  <div className="h-1.5 w-full bg-gradient-to-r from-primary via-secondary to-primary" />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <AppImage
                          src={hospitalConfig.logo}
                          alt="All Is Well Hospital Logo"
                          width={60}
                          height={24}
                          className="h-6 w-auto object-contain"
                        />
                        <div>
                          <p className="text-xs font-bold text-white tracking-widest uppercase leading-none">ALL IS WELL</p>
                          <p className="text-xs text-primary/80 tracking-widest uppercase leading-none mt-0.5">DIGITAL HEALTH PASSPORT™</p>
                        </div>
                      </div>
                      <Shield size={18} className="text-primary/60" />
                    </div>
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-16 h-16 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                        <UserCircle size={32} className="text-primary/60" aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground tracking-widest uppercase mb-1">PATIENT</p>
                        <p className="text-xl font-bold text-white mb-1">{displayName}</p>
                        <p className="text-xs text-muted-foreground font-mono">ID: {patientId}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="w-20 h-20 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
                        <QrCode size={36} className="text-white/40" aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-white/80 mb-1">SECURE QR CODE</p>
                        <p className="text-xs text-muted-foreground/70 leading-relaxed">
                          QR code will be generated after secure sign-in. It contains only a secure token — never your medical data.
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <Lock size={10} className="text-muted-foreground/50" />
                      <p className="text-xs text-muted-foreground/50">
                        This QR opens a secure authenticated workflow only. Medical data is never exposed publicly.
                      </p>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-gradient-to-r from-secondary/50 via-primary/50 to-secondary/50" />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-6 p-4 rounded-xl bg-card/50 border border-border/50"
                >
                  <p className="text-xs font-semibold text-foreground tracking-widest uppercase mb-3">FUTURE QR USES</p>
                  <div className="space-y-2">
                    {['Registration & appointment check-in', 'Visit history retrieval', 'Membership verification', 'Care continuity across centres'].map((use, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <div className="w-1 h-1 rounded-full bg-primary/60 flex-shrink-0" />
                        {use}
                      </div>
                    ))}
                  </div>
                </motion.div>

                <div className="mt-4 p-3 rounded-xl bg-amber-950/30 border border-amber-900/40 flex items-start gap-2">
                  <Info size={12} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-400/70 leading-relaxed">
                    DEMO DATA — This card shows placeholder information only. Real passport requires secure patient authentication.
                  </p>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </main>
  );
}
